<template>
  <div class="p-grid">
    <div class="p-col-12">
      <div class="card">
        <BillingFeatureGate feature="supplier_access"
                            variant="page"
                            :title="$t('Supplier Logins')"
                            :description="$t('billing.features.supplier_access.tooltip')"
                            icon="ti-key">
          <Toolbar class="p-mb-4 p-p-2">
            <template #start>
              <div style="font-size: 1.4em; color: inherit" class="p-p-2">{{ $t('Supplier Logins') }}</div>
            </template>
            <template #end>
              <SearchInput :disabled="dataTableIsLoading"
                           @change-processed-search-value="changeProcessedSearchValue"
                           v-model="searchData"
                           @show-spinner="isLoading = true"/>
            </template>
          </Toolbar>

          <Spinner v-if="isLoading"/>
          <DesktopDataTable v-else
                            :showHeader="false"
                            :dataTableIsLoading="dataTableIsLoading"
                            :itemsArrayIsEmpty="itemsArrayIsEmpty"
                            :sort="sort"
                            :showSearchInput="false"
                            :showButtons="false"
                            :actionButtonsCount="actionButtonsCount"
                            @sort-items="sortItems"
                            :headers="[
              {name: 'name', title: $t('Name'),sortable: true},
              {name: 'favicon', width: '40px', sortable: false},
              {name: 'url', title: 'URL', width: '22%', sortable: true},
              {name: 'username', title: $t('Username'), sortable: false},
              {name: 'password', title: $t('Password'), sortable: false},
              {name: 'comment', title: $t('Comment'), sortable: false},
          ]">
            <template v-slot:body>
              <tr v-for="item of items"
                  :key="item.id"
                  :class="{'updated-item': isItemUpdated(item.id)}">
                <td class="word-break">
                  <span v-if="item.name">{{ item.name }}</span>
                  <i v-else class="ti-minus"/>
                </td>
                <td>
                  <img v-if="item.url" :src="`https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=${encodeURIComponent(normalizeUrl(item.url))}&size=48`" style="height: 20px;width: 20px; display: block" alt="">
                  <i v-else class="ti-minus"/>
                </td>
                <td class="word-break">
                  <div v-if="item.url" style="width: 20vw; max-width: 1160px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                    <a :href="normalizeUrl(item.url)"
                       target="_blank"
                       rel="noopener noreferrer"
                       draggable="false"
                       class="word-break">
                      {{ item.url }}
                    </a>
                  </div>
                  <i v-else class="ti-minus"/>
                </td>
                <td class="word-break">
                  <span v-if="item.username" class="word-break">{{ item.username }}</span>
                  <i v-else class="ti-minus"/>
                </td>
                <td class="word-break">
                  <span v-if="item.password && item.passwordLoaded && !item.passwordIsLoading">{{ item.password }}</span>
                  <i v-else-if="!item.password && item.passwordLoaded && !item.passwordIsLoading" class="ti-minus"></i>
                  <Button v-else @click.prevent="showPassword(item)"
                          class="p-button-sm p-button-text nowrap p-p-1"
                          :icon="'ti-eye'"
                          :loading="item.passwordIsLoading"
                          :label="$t('Show password')"/>
                </td>
                <td class="word-break">
<!--                  <div v-if="item.comment" class="word-break" style="width: 20vw; max-width: 900px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">{{ item.comment }}</div>-->
                  <div v-if="item.comment" class="word-break">{{ item.comment }}</div>
                  <i v-else class="ti-minus"/>
                </td>
              </tr>
            </template>
          </DesktopDataTable>

          <OwnPaginator v-show="items && items.length"
                        :rows="pagination.perPage"
                        :currentPage="pagination.currentPage - 1"
                        :totalPages="pagination.pageCount"
                        :totalRecords="pagination.totalCount"
                        :rowsPerPageOptions="[5,10,25,50,100,200]"
                        :pageLinkSize="11"/>

          <TotalRecordsData :pagination="pagination" :itemsArrayIsEmpty="itemsArrayIsEmpty"/>
        </BillingFeatureGate>
      </div>

    </div>
  </div>
</template>

<script>
import httpClient from '@/services/http.services'
import datatableMixins from "@/mixins/datatableMixins";
import formatMixins from "@/mixins/formatMixins";
import settings from "@/settings";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import BillingFeatureGate from "@/components/billing/BillingFeatureGate.vue";
import billingFeaturesMixins from '@/mixins/billingFeaturesMixins'

export default {
  mixins: [ datatableMixins, formatMixins, showErrorsMixins, billingFeaturesMixins ],
  components: { BillingFeatureGate },
  data() {
    return {
      fieldsExpand: '',
      apiLink: 'link/get-user-links',
      routePath: '/supplier-accounts-data',
      sort: {
        column: 'name',
        direction: '',
      },
    }
  },
  watch: {
    canUseSupplierAccess(allowed, wasAllowed) {
      if (allowed && !wasAllowed) {
        this.$nextTick(() => {
          this.checkRouteQuery()
          this.getPageData()
        })
        return
      }

      if (!allowed && wasAllowed) {
        this.resetSupplierAccountsData()
      }
    },
  },
  mounted() {
    this.scrollToTop()
    if (settings.autoLogoutTimeout && this.$store.state.logoutTimestampIsOverdue) return false
    if (this.canUseSupplierAccess) {
      this.checkRouteQuery()
      this.getPageData()
    }
  },
  methods: {
    encodeURIComponent(value) {
      return encodeURIComponent(value)
    },
    getPageData() {
      if (!this.canUseSupplierAccess) return
      this.getItems()
    },
    resetSupplierAccountsData() {
      this._abortors?.supplierAccountsData?.abort?.()
      this.items = []
      this.pagination.currentPage = 1
      this.pagination.pageCount = 1
      this.pagination.totalCount = 0
      this.isLoading = false
      this.dataTableIsLoading = false
    },
    async showPassword(access) {
      if (!this.canUseSupplierAccess || !access?.id) return
      access.passwordIsLoading = true

      try {
        const { data, status } = await httpClient.post('link/get-password', { id: access.id })
        if (status === 200 && data?.success) {
          access.passwordLoaded = true
          access.password = data.password
        } else {
          return null
        }
      } catch(err) {
        if (!this.canUseSupplierAccess) return
        this.showError(err)
      } finally {
        access.passwordIsLoading = false
      }
    },
    async getItems() {
      if (!this.canUseSupplierAccess || !this.userCanGetData || this.fetchInFlight) return
      this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      this.dataTableIsLoading = true
      const ctrl = this.setController?.('supplierAccountsData')
      try {
        // const { status, data, headers } = await httpClient(`${this.apiLink}?page=${this.pagination.currentPage}&per-page=${this.pagination.perPage}&sort=${this.sort.direction + this.sort.column}&search=${this.searchData}&expand=${this.fieldsExpand}`)
        const { status, data, headers } = await httpClient({
          url: this.apiLink,
          params: {
            page: this.pagination.currentPage,
            'per-page': this.pagination.perPage,
            sort: this.sort.direction + this.sort.column,
            search: this.processedSearchValue ?? null,
            expand: this.fieldsExpand,
          },
          signal: ctrl?.signal
        })
        if (status === 200 && data && Array.isArray(data)) {
          const hasPaginationHeaders = !!headers['x-pagination-total-count']
          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount  = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = data.length
            this.pagination.pageCount  = 1
          }

          this.items = data.map(item => ({
            ...item,
            password: null,
            passwordLoaded: false,
            passwordIsLoading: false,
          }))

          if (!data.length) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }
        } else {
          this.items = []
          this.showUnknownError()
        }
      } catch(err) {
        this.items = []
        if (!this.canUseSupplierAccess) return
        this.showError(err)
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.isLoading = false
        this.dataTableIsLoading = false
        if (this._abortors?.supplierAccountsData === ctrl) {
          this._abortors.supplierAccountsData = null
        }
      }
    },
  },
  computed: {
    actionButtonsCount() {
      return 0
    },
  }
}
</script>

<style scoped lang="scss">
td {
  height: 44px;
}
</style>